import { createAction, props } from '@ngrx/store';
import {
  BackOfficeApplicationsScheduleFilesToDeletePayload,
  BackOfficeApplicationsRestorePayload,
  BackOfficeApplicationsSoftDeletePayload,
  BackOfficeGenerateApplicationReport,
  BackOfficeGenerateTransactionsReport,
  BackOfficeGetApplicationsPayload,
  BackOfficeGetPartnersPayload,
  BackOfficeGetScheduledFilesDeletionPayload,
  BackOfficeGetTransactionsPayload,
  BackOfficeGetUsersPayload,
  BackOfficeUsersActivateAccountPayload,
  BackOfficeUsersChangePasswordPayload,
  BackOfficeUsersChangePasswordReturn,
  BackOfficeUsersDeactivateAccountPayload,
  BackOfficeUsersPermDeletePayload,
  BackOfficeUsersRestoreAccountPayload,
  BackOfficeUsersSoftDeletePayload,
  Company,
  Language,
  PaginatedResponse,
  Purchase,
  Role,
  User,
  UserAsset,
  UserDocument,
  UserProcess,
  BackOfficeGenerateUsersReport,
} from '@interfaces';
import { ScheduledFileDeletion } from '../../_interfaces/scheduled-file-deletion';

export const getRoles = createAction('[BackOffice] Get Roles');
export const getRolesSuccess = createAction('[BackOffice] Get Roles Success', props<{ roles: Role[] }>());
export const getRolesError = createAction('[BackOffice] Get Roles Error', props<{ errorMessage: string }>());

export const getLanguages = createAction('[BackOffice] Get Languages');
export const getLanguagesSuccess = createAction(
  '[BackOffice] Get Languages Success',
  props<{ languages: Language[] }>()
);
export const getLanguagesError = createAction('[BackOffice] Get Languages Error', props<{ errorMessage: string }>());

export const getCompanies = createAction('[BackOffice] Get Companies');
export const getCompaniesSuccess = createAction(
  '[BackOffice] Get Companies Success',
  props<{ companies: Company[] }>()
);
export const getCompaniesError = createAction('[BackOffice] Get Companies Error', props<{ errorMessage: string }>());

export const getEmployees = createAction('[BackOffice] Get Employees');
export const getEmployeesSuccess = createAction('[BackOffice] Get Employees Success', props<{ employees: User[] }>());
export const getEmployeesError = createAction('[BackOffice] Get Employees Error', props<{ errorMessage: string }>());

export const getPartners = createAction(
  '[BackOffice] Get Partners',
  props<{ payload: BackOfficeGetPartnersPayload }>()
);
export const getPartnersSuccess = createAction(
  '[BackOffice] Get Partners Success',
  props<{ partners: Partial<User>[] }>()
);
export const getPartnersError = createAction('[BackOffice] Get Partners Error', props<{ errorMessage: string }>());

export const setSelectedPartners = createAction(
  '[BackOffice] Set Selected Users',
  props<{ payload: { selectedPartners: Partial<User>[] } }>()
);

// ############
// USERS
// ############

export const getUsers = createAction('[BackOffice] Get Users', props<{ payload: BackOfficeGetUsersPayload }>());
export const getUsersSuccess = createAction(
  '[BackOffice] Get Users Success',
  props<{ users: PaginatedResponse<User> }>()
);
export const getUsersError = createAction('[BackOffice] Get Users Error', props<{ errorUsersMessage: string }>());

export const usersActivateAccount = createAction(
  '[BackOffice] Activate Account',
  props<{ payload: BackOfficeUsersActivateAccountPayload }>()
);
export const usersActivateAccountSuccess = createAction(
  '[BackOffice] Activate Account Success',
  props<{ activatedAccountID: string }>()
);
export const usersActivateAccountError = createAction(
  '[BackOffice] Activate Account Error',
  props<{ errorUsersMessage: string }>()
);

export const usersDeactivateAccount = createAction(
  '[BackOffice] Deactivate Account',
  props<{ payload: BackOfficeUsersDeactivateAccountPayload }>()
);
export const usersDeactivateAccountSuccess = createAction(
  '[BackOffice] Deactivate Account Success',
  props<{ deactivatedAccountID: string }>()
);
export const usersDeactivateAccountError = createAction(
  '[BackOffice] Deactivate Account Error',
  props<{ errorUsersMessage: string }>()
);

export const usersRestoreAccount = createAction(
  '[BackOffice] Restore Account',
  props<{ payload: BackOfficeUsersRestoreAccountPayload }>()
);
export const usersRestoreAccountSuccess = createAction(
  '[BackOffice] Restore Account Success',
  props<{ restoredAccountID: string }>()
);
export const usersRestoreAccountError = createAction(
  '[BackOffice] Restore Account Error',
  props<{ errorUsersMessage: string }>()
);

export const usersSoftDelete = createAction(
  '[BackOffice] Soft Delete',
  props<{ payload: BackOfficeUsersSoftDeletePayload }>()
);
export const usersSoftDeleteSuccess = createAction(
  '[BackOffice] Soft Delete Success',
  props<{ deletedUsersID: string[] }>()
);
export const usersSoftDeleteError = createAction(
  '[BackOffice] Soft Delete Error',
  props<{ errorUsersMessage: string }>()
);

export const usersPermDelete = createAction(
  '[BackOffice] Permanent Delete',
  props<{ payload: BackOfficeUsersPermDeletePayload }>()
);
export const usersPermDeleteSuccess = createAction(
  '[BackOffice] Permanent Delete Success',
  props<{ deletedUsersID: string[] }>()
);
export const usersPermDeleteError = createAction(
  '[BackOffice] Permanent Delete Error',
  props<{ errorUsersMessage: string }>()
);

export const usersChangePassword = createAction(
  '[BackOffice] Change Password Generate Random',
  props<{ payload: BackOfficeUsersChangePasswordPayload }>()
);
export const usersChangePasswordSuccess = createAction(
  '[BackOffice] Change Password Generate Random Success',
  props<BackOfficeUsersChangePasswordReturn>()
);
export const usersChangePasswordError = createAction(
  '[BackOffice] Change Password Generate Random Error',
  props<{ errorUsersMessage: string }>()
);

export const usersGenerateReport = createAction(
  '[BackOffice] Generate Users Report',
  props<{ payload: BackOfficeGenerateUsersReport }>()
);
export const usersGenerateReportSuccess = createAction('[BackOffice] Generate Users Report Success');
export const usersGenerateReportError = createAction(
  '[BackOffice] Generate Users Report Error',
  props<{ errorUsersMessage: string }>()
);

// ############
// TRANSACTIONS
// ############

export const getTransactions = createAction(
  '[BackOffice] Get Transactions',
  props<{ payload: BackOfficeGetTransactionsPayload }>()
);
export const getTransactionsSuccess = createAction(
  '[BackOffice] Get Transactions Success',
  props<{ transactions: PaginatedResponse<Purchase> }>()
);
export const getTransactionsError = createAction(
  '[BackOffice] Get Transactions Error',
  props<{ errorTransactionsMessage: string }>()
);

export const transactionsDownloadPurchaseDocument = createAction(
  '[BackOffice] Download Purchase Document',
  props<{ transactionID: string; previewTargetKey?: string }>()
);
export const transactionsDownloadPurchaseDocumentSuccess = createAction(
  '[BackOffice] Download Purchase Document Success'
);
export const transactionsDownloadPurchaseDocumentError = createAction(
  '[BackOffice] Download Purchase Document Error',
  props<{ errorTransactionsMessage: string }>()
);

export const transactionsGenerateReport = createAction(
  '[BackOffice] Generate Transactions Report',
  props<{ payload: BackOfficeGenerateTransactionsReport }>()
);
export const transactionsGenerateReportSuccess = createAction('[BackOffice] Generate Transactions Report Success');
export const transactionsGenerateReportError = createAction(
  '[BackOffice] Generate Transactions Report Error',
  props<{ errorTransactionsMessage: string }>()
);

// ############
// APPLICATIONS
// ############

export const getApplications = createAction(
  '[BackOffice] Get Applications',
  props<{ payload: BackOfficeGetApplicationsPayload }>()
);
export const getApplicationsSuccess = createAction(
  '[BackOffice] Get Applications Success',
  props<{ applications: PaginatedResponse<UserProcess> }>()
);
export const getApplicationsError = createAction(
  '[BackOffice] Get Applications Error',
  props<{ errorApplicationsMessage: string }>()
);

export const applicationsGenerateReport = createAction(
  '[BackOffice] Generate Application Report',
  props<{ payload: BackOfficeGenerateApplicationReport }>()
);
export const applicationsGenerateReportSuccess = createAction('[BackOffice] Generate Application Report Success');
export const applicationsGenerateReportError = createAction(
  '[BackOffice] Generate Application Report Error',
  props<{ errorApplicationsMessage: string }>()
);

export const applicationsSoftDelete = createAction(
  '[BackOffice] Applications Soft Delete',
  props<{ payload: BackOfficeApplicationsSoftDeletePayload }>()
);
export const applicationsSoftDeleteSuccess = createAction(
  '[BackOffice] Applications Soft Delete Success',
  props<{ deletedApplicationsID: string[] }>()
);
export const applicationsSoftDeleteError = createAction(
  '[BackOffice] Applications Soft Delete Error',
  props<{ errorApplicationsMessage: string }>()
);

export const applicationsRestore = createAction(
  '[BackOffice] Applications Restore',
  props<{ payload: BackOfficeApplicationsRestorePayload }>()
);
export const applicationsRestoreSuccess = createAction(
  '[BackOffice] Applications Restore Success',
  props<{ deletedApplicationID: string }>()
);
export const applicationsRestoreeError = createAction(
  '[BackOffice] Applications Restore Error',
  props<{ errorApplicationsMessage: string }>()
);

export const scheduleUserProcessesFilesToDelete = createAction(
  '[BackOffice] Applications Schedule Files To Delete',
  props<{ payload: BackOfficeApplicationsScheduleFilesToDeletePayload }>()
);
export const scheduleUserProcessesFilesToDeleteSuccess = createAction(
  '[BackOffice] Applications Schedule Files To Delete Success',
  props<{ scheduledUserProcessesFilesToDelete: string[] }>()
);
export const scheduleUserProcessesFilesToDeleteError = createAction(
  '[BackOffice] Applications Schedule Files To Delete Error',
  props<{ errorApplicationsMessage: string }>()
);

// ############
// SCHEDULED FILES DELETION
// ############

export const getScheduledFilesDeletion = createAction(
  '[BackOffice] Get Scheduled Files Deletion',
  props<{ payload: BackOfficeGetScheduledFilesDeletionPayload }>()
);
export const getScheduledFilesDeletionSuccess = createAction(
  '[BackOffice] Get Scheduled Files Deletion Success',
  props<{ scheduledFilesDeletion: PaginatedResponse<ScheduledFileDeletion> }>()
);
export const getScheduledFilesDeletionError = createAction(
  '[BackOffice] Get Scheduled Files Deletion Error',
  props<{ errorScheduledFilesDeletionMessage: string }>()
);

export const deleteScheduledFiles = createAction(
  '[BackOffice] Delete Scheduled Files',
  props<{ payload: { filesIdsToDelete: string[] } }>()
);
export const deleteScheduledFilesSuccess = createAction(
  '[BackOffice] Delete Scheduled Files Success',
  props<{ scheduledFileDeletion: ScheduledFileDeletion[] }>()
);
export const deleteScheduledFilesError = createAction(
  '[BackOffice] Delete Scheduled Files Error',
  props<{ errorScheduledFilesDeletionMessage: string }>()
);

export const restoreScheduledFiles = createAction(
  '[BackOffice] Restore Scheduled Files',
  props<{ payload: { filesIdsToRestore: string[] } }>()
);
export const restoreScheduledFilesSuccess = createAction(
  '[BackOffice] Restore Scheduled Files Success',
  props<{ scheduledFileDeletion: ScheduledFileDeletion[] }>()
);
export const restoreScheduledFilesError = createAction(
  '[BackOffice] Restore Scheduled Files Error',
  props<{ errorScheduledFilesDeletionMessage: string }>()
);

// ############
// MANAGE FILES
// ############

export const getUserProcessBasicInformation = createAction(
  '[BackOffice] Get UserProcess Basic Information',
  props<{ userProcessId: string }>()
);
export const getUserProcessBasicInformationSuccess = createAction(
  '[BackOffice] Get UserProcess Basic Information Success',
  props<{ userProcess: UserProcess }>()
);
export const getUserProcessBasicInformationError = createAction(
  '[BackOffice] Get UserProcess Basic Information Error',
  props<{ errorManageFilesMessage: string }>()
);

export const getUserProcessManageFiles = createAction(
  '[BackOffice] Get UserProcess Manage Files',
  props<{ userProcessId: string }>()
);
export const getUserProcessManageFilesSuccess = createAction(
  '[BackOffice] Get UserProcess Manage Files Success',
  props<{
    userProcessFiles: {
      userDocuments: UserDocument[];
      userAssets: UserAsset[];
    };
  }>()
);
export const getUserProcessManageFilesError = createAction(
  '[BackOffice] Get UserProcess Manage Files Error',
  props<{ errorManageFilesMessage: string }>()
);

export const manageFilesDownload = createAction(
  '[BackOffice] Manage Files Download',
  props<{
    payload: {
      userProcessId: string;
      fileId: string;
      fileName: string;
      fileType: 'userDocuments' | 'userAssets';
    };
  }>()
);
export const manageFilesDownloadSuccess = createAction('[BackOffice] Manage Files Download Success');
export const manageFilesDownloadError = createAction(
  '[BackOffice] Manage Files Download Error',
  props<{ errorManageFilesMessage: string }>()
);

export const manageFilesScheduleToDelete = createAction(
  '[BackOffice] Manage Files Schedule To Delete',
  props<{
    payload: { userProcessId: string; fileId: string; fileName: string; fileType: 'userDocuments' | 'userAssets' };
  }>()
);
export const manageFilesScheduleToDeleteSuccess = createAction(
  '[BackOffice] Manage Files Schedule To Delete Success',
  props<{ fileId: string; fileName: string; fileType: 'userDocuments' | 'userAssets' }>()
);
export const manageFilesScheduleToDeleteError = createAction(
  '[BackOffice] Manage Files Schedule To Delete Error',
  props<{ errorManageFilesMessage: string }>()
);

export const manageFilesScheduleToDeleteAll = createAction(
  '[BackOffice] Manage Files Schedule To Delete All',
  props<{
    payload: { userProcessId: string };
  }>()
);
export const manageFilesScheduleToDeleteAllSuccess = createAction(
  '[BackOffice] Manage Files Schedule To Delete All Success',
  props<{ scheduledFilesDeletion: ScheduledFileDeletion[] }>()
);
export const manageFilesScheduleToDeleteAllError = createAction(
  '[BackOffice] Manage Files Schedule To Delete All Error',
  props<{ errorManageFilesMessage: string }>()
);

export const manageFilesRestore = createAction(
  '[BackOffice] Manage Files Restore',
  props<{
    payload: { userProcessId: string; fileId: string; fileName: string; fileType: 'userDocuments' | 'userAssets' };
  }>()
);
export const manageFilesRestoreSuccess = createAction(
  '[BackOffice] Manage Files Restore Success',
  props<{ fileId: string; fileName: string; fileType: 'userDocuments' | 'userAssets' }>()
);
export const manageFilesRestoreError = createAction(
  '[BackOffice] Manage Files Restore Error',
  props<{ errorManageFilesMessage: string }>()
);

export const manageFilesRestoreAll = createAction(
  '[BackOffice] Manage Files Restore All',
  props<{
    payload: { userProcessId: string };
  }>()
);
export const manageFilesRestoreAllSuccess = createAction(
  '[BackOffice] Manage Files Restore All Success',
  props<{ success: boolean; deletedCount: number }>()
);
export const manageFilesRestoreAllError = createAction(
  '[BackOffice] Manage Files Restore All Error',
  props<{ errorManageFilesMessage: string }>()
);
